// mobile/pages/administration/ProfileSetup.tsx

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../../shared/api/axios";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";
import StartFooter from "./StartFooter";

type HandleStatus =
  | "idle"
  | "checking"
  | "unused"
  | "used"
  | "too_short"
  | "invalid_chars"
  | "error";

type EmailStatus =
  | "idle"
  | "checking"
  | "invalid"
  | "used"
  | "unused"
  | "error";

const ProfileSetup = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [profilename, setProfilename] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isOver18, setIsOver18] = useState(false);
  const [loading, setLoading] = useState(false);

  const [handleStatus, setHandleStatus] = useState<HandleStatus>("idle");
  const debTimer = useRef<number | null>(null);
  const lastQueried = useRef("");

  const [emailStatus, setEmailStatus] = useState<EmailStatus>("idle");
  const emailDebTimer = useRef<number | null>(null);
  const lastEmailQueried = useRef("");

  useEffect(() => {
    const trimmed = profilename.trim();

    if (!trimmed) {
      setHandleStatus("idle");
      return;
    }

    if (trimmed.length < 3) {
      setHandleStatus("too_short");
      return;
    }

    if (!/^[A-Za-z0-9_-]{3,18}$/.test(trimmed)) {
      setHandleStatus("invalid_chars");
      return;
    }

    setHandleStatus("checking");

    if (debTimer.current) window.clearTimeout(debTimer.current);

    debTimer.current = window.setTimeout(async () => {
      try {
        lastQueried.current = trimmed;

        const { data } = await api.get("/users/check-handle", {
          params: { profilename: lastQueried.current },
        });

        if (lastQueried.current === trimmed) {
          setHandleStatus(
            data?.status === "unused"
              ? "unused"
              : data?.status === "used"
              ? "used"
              : data?.status === "too_short"
              ? "too_short"
              : data?.status === "invalid_chars"
              ? "invalid_chars"
              : "error"
          );
        }
      } catch {
        if (lastQueried.current === trimmed) setHandleStatus("error");
      }
    }, 350) as unknown as number;

    return () => {
      if (debTimer.current) window.clearTimeout(debTimer.current);
    };
  }, [profilename]);

  const emailTrim = email.trim().toLowerCase();

  useEffect(() => {
    if (!emailTrim) {
      setEmailStatus("idle");
      return;
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);

    if (!emailOk) {
      setEmailStatus("invalid");
      return;
    }

    setEmailStatus("checking");

    if (emailDebTimer.current) window.clearTimeout(emailDebTimer.current);

    emailDebTimer.current = window.setTimeout(async () => {
      try {
        lastEmailQueried.current = emailTrim;

        const { data } = await api.get("/users/check-email", {
          params: { email: lastEmailQueried.current },
        });

        if (lastEmailQueried.current === emailTrim) {
          setEmailStatus(
            data?.status === "unused"
              ? "unused"
              : data?.status === "used"
              ? "used"
              : data?.status === "invalid"
              ? "invalid"
              : "error"
          );
        }
      } catch {
        if (lastEmailQueried.current === emailTrim) setEmailStatus("error");
      }
    }, 350) as unknown as number;

    return () => {
      if (emailDebTimer.current) window.clearTimeout(emailDebTimer.current);
    };
  }, [emailTrim]);

  const pw = password || "";
  const pwChecks = {
    len: pw.length >= 8,
    digit: /\d/.test(pw),
  };

  const pwValid = pwChecks.len && pwChecks.digit;

  const canSubmit =
    !!profilename &&
    handleStatus === "unused" &&
    emailStatus === "unused" &&
    pwValid &&
    isOver18 &&
    !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);

    try {
      await api.post("/auth/register", {
        profilename: profilename.trim(),
        email: emailTrim,
        password,
        language: i18n.language,
      });

     sessionStorage.setItem("registrationFlow", "normal");
sessionStorage.setItem("registrationReturnTo", "/gallery");

navigate("/check-email", {
  state: {
    email: emailTrim,
    flow: "normal",
    returnTo: "/gallery",
  },
});
    } catch (err: any) {
      console.error("❌ Registration error:", err);
      const msg =
        err?.response?.data?.msg ??
        t("profileSetup.registerError") ??
        "⚠️ Registration failed. Please try again.";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

 return (
  <div style={pageShell}>
    <header style={logoHeaderStyle}>
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label="Go to homepage"
        style={logoButtonStyle}
      >
        <HighLogo
          src={logo}
          style={{
            width: 170,
            maxWidth: "62vw",
            display: "block",
          }}
          padding={0}
        />
      </button>
    </header>

    <main style={mainStyle}>
      <section style={signupPanel}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          style={backBtn}
        >
          ‹
        </button>

        <div style={titleWrap}>
          <h1 style={titleStyle}>
            {t("profileSetup.title") || "Create your profile"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldGroup}>
            <label htmlFor="profilename" style={labelStyle}>
              {t("profileSetup.profileName")}
            </label>

            <input
              id="profilename"
              type="text"
              value={profilename}
              onChange={(event) => setProfilename(event.target.value)}
              placeholder={t("profileSetup.profileName")}
              style={inputModern}
              autoComplete="username"
              maxLength={18}
              required
            />

            <div style={statusLine}>
              {handleStatus === "unused" && (
                <span style={successStatus}>
                  ✓ Profile name available
                </span>
              )}

              {handleStatus === "used" && (
                <span style={errorStatus}>
                  ✕ This name is already taken
                </span>
              )}

              {handleStatus === "too_short" && (
                <span style={errorStatus}>
                  ✕ Too short. Minimum 3 characters.
                </span>
              )}

              {handleStatus === "invalid_chars" && (
                <span style={errorStatus}>
                  ✕ Only letters, numbers, _ and -
                </span>
              )}

              {handleStatus === "checking" && (
                <span style={checkingStatus}>Checking...</span>
              )}

              {handleStatus === "error" && (
                <span style={errorStatus}>
                  ✕ Unable to check profile name
                </span>
              )}
            </div>
          </div>

          <div style={fieldGroup}>
            <label htmlFor="email" style={labelStyle}>
              {t("profileSetup.email")}
            </label>

            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t("profileSetup.emailplace")}
              style={inputModern}
              autoComplete="email"
              required
            />

            <div style={statusLine}>
              {emailStatus === "unused" && (
                <span style={successStatus}>✓ Email available</span>
              )}

              {emailStatus === "used" && (
                <span style={errorStatus}>
                  ✕ This email is already used
                </span>
              )}

              {emailStatus === "invalid" && (
                <span style={errorStatus}>
                  ✕ Invalid email address
                </span>
              )}

              {emailStatus === "checking" && (
                <span style={checkingStatus}>Checking...</span>
              )}

              {emailStatus === "error" && (
                <span style={errorStatus}>
                  ✕ Unable to check email
                </span>
              )}
            </div>
          </div>

          <div style={fieldGroup}>
            <label htmlFor="password" style={labelStyle}>
              {t("profileSetup.password")}
            </label>

            <input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("profileSetup.createpassword")}
              style={inputModern}
              autoComplete="new-password"
              required
            />

            <div style={passwordStatusArea}>
              {password.length > 0 && (
                <div style={passwordChecks}>
                  <span
                    style={{
                      color: pwChecks.len ? "#22c55e" : "#f97373",
                    }}
                  >
                    {pwChecks.len ? "✓" : "✕"} At least 8 characters
                  </span>

                  <span
                    style={{
                      color: pwChecks.digit ? "#22c55e" : "#f97373",
                    }}
                  >
                    {pwChecks.digit ? "✓" : "✕"} At least one number
                  </span>
                </div>
              )}
            </div>
          </div>

          <label htmlFor="over18" style={ageRow}>
            <input
              id="over18"
              type="checkbox"
              checked={isOver18}
              onChange={(event) => setIsOver18(event.target.checked)}
              style={checkboxStyle}
            />

            <span>{t("profileSetup.over18")}</span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            style={canSubmit ? submitBtn : submitBtnDisabled}
          >
            {loading
              ? t("profileSetup.creating") || "Creating..."
              : t("profileSetup.createProfile") || "Create Profile"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/start")}
            style={loginLink}
          >
            {t("profileSetup.alreadyAccount") ||
              "Already have an account? Log in"}
          </button>

          <div style={legalBlock}>
            <div style={{ marginBottom: 8 }}>
              {t(
                "profileSetup.legalNotice",
                "By creating an account, you agree to our policies."
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate("/legal/privacypolicy")}
              style={legalLink}
            >
              {t("profileSetup.privacyPolicy", "Privacy Policy")}
            </button>

            <span aria-hidden="true"> · </span>

            <button
              type="button"
              onClick={() => navigate("/legal/termsofuse")}
              style={legalLink}
            >
              {t("profileSetup.termsOfUse", "Terms of Use")}
            </button>
          </div>
        </form>
      </section>
    </main>

    <StartFooter />
  </div>
);
};
const pageShell: React.CSSProperties = {
  minHeight: "100dvh",
  width: "100%",
  overflowX: "hidden",
  background:
    "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#f9fafb",
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: "flex",
  flexDirection: "column",
};

const logoHeaderStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding:
    "calc(24px + env(safe-area-inset-top)) 20px 8px",
  boxSizing: "border-box",
};

const logoButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  touchAction: "manipulation",
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 20px 54px",
  boxSizing: "border-box",
};

const signupPanel: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: 430,
  paddingTop: 22,
  boxSizing: "border-box",
};

const backBtn: React.CSSProperties = {
  position: "absolute",
  top: 4,
  left: -8,
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  borderRadius: "50%",
  background: "transparent",
  color: "#f8e298",
  cursor: "pointer",
  fontSize: 38,
  lineHeight: 1,
  touchAction: "manipulation",
};

const titleWrap: React.CSSProperties = {
  width: "100%",
  textAlign: "center",
  marginBottom: 40,
  marginTop: -17,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  padding: "0 34px",
  color: "#f8e298",
  fontSize: "clamp(29px, 8vw, 34px)",
  lineHeight: 1.08,
  letterSpacing: "-1.1px",
  fontWeight: 800,
  
};

const formStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 18,
};

const fieldGroup: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const labelStyle: React.CSSProperties = {
  marginLeft: 4,
  color: "#94a3b8",
  fontSize: 14,
  fontWeight: 600,
};

const inputModern: React.CSSProperties = {
  width: "100%",
  height: 52,
  padding: "0 18px",
  borderRadius: 16,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(15,23,42,0.45)",
  color: "#f8fafc",
  fontSize: 16,
  outline: "none",
  boxSizing: "border-box",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
};

const statusLine: React.CSSProperties = {
  minHeight: 18,
  paddingLeft: 4,
  fontSize: 12,
  lineHeight: 1.4,
};

const successStatus: React.CSSProperties = {
  color: "#22c55e",
};

const errorStatus: React.CSSProperties = {
  color: "#f97373",
};

const checkingStatus: React.CSSProperties = {
  color: "#9ca3af",
};

const passwordStatusArea: React.CSSProperties = {
  minHeight: 34,
};

const passwordChecks: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  paddingLeft: 4,
  fontSize: 12,
  lineHeight: 1.4,
};

const ageRow: React.CSSProperties = {
  minHeight: 42,
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "#cbd5e1",
  fontSize: 14,
  cursor: "pointer",
};

const checkboxStyle: React.CSSProperties = {
  width: 18,
  height: 18,
  margin: 0,
  accentColor: "#d4af37",
  cursor: "pointer",
  flexShrink: 0,
};

const submitBtn: React.CSSProperties = {
  width: "100%",
  minHeight: 50,
  marginTop: 4,
  padding: "13px 18px",
  borderRadius: 999,
  border: "1px solid rgba(250,250,250,0.22)",
  background:
    "linear-gradient(135deg, rgba(212,175,55,0.98), rgba(250,250,210,0.92))",
  color: "#111827",
  fontWeight: 800,
  fontSize: 15,
  cursor: "pointer",
  boxShadow: "0 12px 30px rgba(212,175,55,0.15)",
  touchAction: "manipulation",
};

const submitBtnDisabled: React.CSSProperties = {
  ...submitBtn,
  border: "1px solid rgba(148,163,184,0.18)",
  background: "rgba(75,85,99,0.55)",
  color: "#94a3b8",
  boxShadow: "none",
  cursor: "not-allowed",
};

const loginLink: React.CSSProperties = {
  alignSelf: "center",
  minHeight: 36,
  padding: "6px 10px",
  border: "none",
  background: "transparent",
  color: "#93c5fd",
  cursor: "pointer",
  fontSize: 13,
  textAlign: "center",
  touchAction: "manipulation",
};

const legalBlock: React.CSSProperties = {
  marginTop: 2,
  textAlign: "center",
  color: "#64748b",
  fontSize: 10.5,
  lineHeight: 1.55,
};

const legalLink: React.CSSProperties = {
  padding: 0,
  border: "none",
  background: "transparent",
  color: "#94a3b8",
  cursor: "pointer",
  font: "inherit",
  touchAction: "manipulation",
};

export default ProfileSetup;